import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { addDays, addMonths, format, isSameDay, parseISO, startOfMonth, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { ChevronLeftIcon, ChevronRightIcon, MapPinIcon } from 'lucide-react';
import { EncabezadoPagina } from '../components/layout/AppLayout';
import { EstadoVacio, Panel } from '../components/ui/Basicos';
import { CalendarioMes } from '../components/calendario/CalendarioMes';
import { MarcaGoogle } from '../components/ui/Google';
import { useDatos } from '../contexts/DatosContext';
import { abogadoPorId } from '../data/abogados';
import { capitalizar, HOY } from '../utils/fechas';
type Vista = 'mes' | 'semana';
export function Calendario() {
  const {
    audiencias,
    expedientes,
    sucursalActiva,
    abogados,
    eventosGoogle
  } = useDatos();
  const sincronizados = useMemo(() => new Set(eventosGoogle.filter(e => e.origen === 'audiencia').map(e => e.origenId)), [eventosGoogle]);
  const [vista, setVista] = useState<Vista>('mes');
  const [referencia, setReferencia] = useState<Date>(HOY);
  const [abogadoId, setAbogadoId] = useState('todos');
  const filtradas = useMemo(() => audiencias.filter(a => {
    if (sucursalActiva !== 'todas' && a.sucursalId !== sucursalActiva) return false;
    if (abogadoId !== 'todos' && a.abogadoId !== abogadoId) return false;
    return true;
  }), [audiencias, sucursalActiva, abogadoId]);
  const diasSemana = useMemo(() => {
    const inicio = startOfWeek(referencia, {
      weekStartsOn: 1
    });
    return Array.from({
      length: 7
    }).map((_, i) => addDays(inicio, i));
  }, [referencia]);
  const mover = (direccion: number) => {
    setReferencia(prev => vista === 'mes' ? addMonths(prev, direccion) : addDays(prev, direccion * 7));
  };
  const titulo = vista === 'mes' ? capitalizar(format(startOfMonth(referencia), 'LLLL yyyy', {
    locale: es
  })) : `Semana del ${format(diasSemana[0], "d 'de' LLLL", {
    locale: es
  })}`;
  return <>
      <EncabezadoPagina titulo="Calendario" descripcion="Audiencias y citas del despacho. El ícono de Google marca los eventos espejeados en el calendario personal del abogado." acciones={<>
            <select aria-label="Filtrar por abogado" value={abogadoId} onChange={e => setAbogadoId(e.target.value)} className="h-9 rounded border border-gris-200 bg-white px-2.5 text-sm text-gris-700" data-id="element-1087" data-mp="q48mjt9vkrr5">
              <option value="todos" data-id="element-1088" data-mp="h9twqwrpxyts">Todos los abogados</option>
              {abogados.map(a => <option key={a.id} value={a.id} data-id="element-1089" data-mp="capqof03ch7o">
                  {a.nombre}
                </option>)}
            </select>
            <div className="flex h-9 items-center rounded border border-gris-200 bg-white p-0.5" data-id="element-1090" data-mp="2f5tlp9sjchc">
              {(['mes', 'semana'] as Vista[]).map(v => <button key={v} type="button" onClick={() => setVista(v)} className={`h-8 rounded px-3 text-xs font-semibold capitalize transition-colors duration-150 ${vista === v ? 'bg-marino-800 text-white' : 'text-gris-600 hover:text-marino-700'}`} data-id="element-1091" data-mp="3ukjgz90nfm3s">
                  {v}
                </button>)}
            </div>
          </>} data-id="element-1086" data-mp="150pwcg2f1ybl" />

      <Panel data-id="element-1092" data-mp="1qe8804c3xczj">
        <div className="flex items-center justify-between border-b border-gris-200 px-4 py-3" data-id="element-1093" data-mp="1qakhle1yl6p1">
          <div className="flex items-center gap-2" data-id="element-1094" data-mp="1v95htdh7nqdh">
            <button type="button" onClick={() => mover(-1)} aria-label="Periodo anterior" className="flex h-8 w-8 items-center justify-center rounded border border-gris-200 text-gris-600 transition-colors duration-150 hover:border-marino-300 hover:text-marino-700" data-id="element-1095" data-mp="209jq8wmq52c1">
              <ChevronLeftIcon className="h-4 w-4" data-id="element-1096" data-mp="1cixjr9sdbq45" />
            </button>
            <button type="button" onClick={() => mover(1)} aria-label="Periodo siguiente" className="flex h-8 w-8 items-center justify-center rounded border border-gris-200 text-gris-600 transition-colors duration-150 hover:border-marino-300 hover:text-marino-700" data-id="element-1097" data-mp="3eazdnqugvktw">
              <ChevronRightIcon className="h-4 w-4" data-id="element-1098" data-mp="3j7g0mhpxjt5e" />
            </button>
            <h2 className="ml-2 text-sm font-semibold text-marino-800" data-id="element-1099" data-mp="1r7z74mjr4ld">{titulo}</h2>
          </div>
          <button type="button" onClick={() => setReferencia(HOY)} className="h-8 rounded border border-gris-200 px-3 text-xs font-medium text-gris-600 transition-colors duration-150 hover:border-marino-300 hover:text-marino-700" data-id="element-1100" data-mp="3sykhxo32vq3e">
            Hoy
          </button>
        </div>

        {vista === 'mes' ? <CalendarioMes mes={referencia} audiencias={filtradas} sincronizados={sincronizados} data-id="element-1101" data-mp="27m95tvqgtdur" /> : <div className="divide-y divide-gris-100" data-id="element-1102" data-mp="n3f90ymcrlku">
            {diasSemana.map(dia => {
          const delDia = filtradas.filter(a => isSameDay(parseISO(a.fecha), dia)).sort((a, b) => a.hora.localeCompare(b.hora));
          return <div key={dia.toISOString()} className="flex gap-6 px-5 py-3.5" data-id="element-1103" data-mp="icglbdz1bl7k">
                  <div className="w-32 shrink-0" data-id="element-1104" data-mp="4dgzee2wpkf3">
                    <p className={`text-xs font-semibold uppercase ${isSameDay(dia, HOY) ? 'text-marino-700' : 'text-gris-400'}`} data-id="element-1105" data-mp="4bmdoxjt9o9y">
                      {format(dia, 'EEEE', {
                  locale: es
                })}
                    </p>
                    <p className="tabular text-2xl font-semibold text-gris-700" data-id="element-1106" data-mp="3vk0ggazc66lu">
                      {format(dia, 'dd')}
                    </p>
                  </div>
                  <div className="flex-1" data-id="element-1107" data-mp="1tr3zk7iqw9tt">
                    {delDia.length === 0 ? <p className="py-2 text-xs text-gris-400" data-id="element-1108" data-mp="32pc61nw837dg">Sin eventos programados</p> : <ul className="space-y-2" data-id="element-1109" data-mp="2xp7odujkw6ln">
                        {delDia.map(a => {
                  const exp = expedientes.find(e => e.id === a.expedienteId);
                  return <li key={a.id} data-id="element-1110" data-mp="2or5h4x994hpx">
                              <Link to={`/expedientes/${a.expedienteId}`} className="flex items-start gap-4 rounded border border-gris-200 px-3 py-2.5 transition-colors duration-150 hover:border-marino-300 hover:bg-gris-50" data-id="element-1111" data-mp="3lfbohrxk5o7w">
                                <span className={`mt-0.5 h-full w-1 shrink-0 self-stretch rounded ${a.tipo === 'Audiencia' ? 'bg-marino-800' : 'bg-ambar-400'}`} aria-hidden="true" data-id="element-1112" data-mp="37n3xkgsygrh4" />
                                <span className="tabular w-14 shrink-0 text-sm font-semibold text-marino-800" data-id="element-1113" data-mp="bkxrad4kae16">
                                  {a.hora}
                                </span>
                                <span className="min-w-0 flex-1" data-id="element-1114" data-mp="livagm7kdkmx">
                                  <span className="flex items-center gap-2 text-sm font-medium text-gris-800" data-id="element-1115" data-mp="pj5hm273eh3d">
                                    {a.titulo}
                                    {sincronizados.has(a.id) && <MarcaGoogle titulo="Sincronizado con Google Calendar" data-id="element-1116" data-mp="zegwckii3ob4" />}
                                  </span>
                                  <span className="mt-0.5 flex items-center gap-1.5 text-xs text-gris-500" data-id="element-1117" data-mp="1951wwdhqj2wo">
                                    <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1118" data-mp="1d9j9nspogfxy" />
                                    {a.lugar} · {a.autoridad}
                                  </span>
                                  <span className="mt-0.5 block text-2xs text-gris-400" data-id="element-1119" data-mp="23gf27cd0jehh">
                                    <span className="font-mono text-marino-700" data-id="element-1120" data-mp="1yi3rz0it31yq">
                                      {exp?.localizador}
                                    </span>{' '}
                                    · {exp?.cliente} · {abogadoPorId(a.abogadoId)?.nombre}
                                  </span>
                                </span>
                              </Link>
                            </li>;
                })}
                      </ul>}
                  </div>
                </div>;
        })}
          </div>}

        {filtradas.length === 0 && <EstadoVacio mensaje="Sin audiencias ni citas con estos filtros" sugerencia="Prueba con otro abogado o cambia la sucursal en la barra superior." data-id="element-1121" data-mp="120r22gwu7dpm" />}
      </Panel>
    </>;
}